import React, { useRef, useState } from 'react';
import { ISelfAssessmentQuestionCard } from '../../interfaces/ISelfAssessmentQuestionCard';
import { useTranslation } from 'react-i18next';
import {
  ButtonWithIcon,
  Card,
  DropdownButton,
  IconContent,
  IconSection,
  ImageButtonContainer,
  Question,
  Tag,
  TagSection,
  TextAreaContainer,
} from '../../../myAudit/components/QuestionCard/styled.questionCard';
import { DropdownIcon } from '../../../common/icons/DropdownIcon';

import { Like } from '../../../common/icons/Like';
import { Dislike } from '../../../common/icons/Dislike';
import { Note } from '../../../common/icons/Note';

import { TextArea } from '../../../common/sharedComponents/TextArea';

import { StandardsBottomSheet } from '../../../myAudit/components/QuestionCard/StandardsBottomSheet';
import { defaultScoringTypes } from '../../../myAudit/components/QuestionCard/utils';
import { nonRenderableScoringTypes } from '../../../../enums/PerformanceClassification';

export function SelfAssessmentQuestionCard ({
                                              question,
                                              onClickScore,
                                              handleQuestionComment,
                                              showDropdownIcon,
                                              isView,
                                            }: ISelfAssessmentQuestionCard) {
  const { t } = useTranslation();
  const inputRef = useRef<HTMLInputElement>(null);
  const [standardCommentOpen, setStandardCommentputOpen] = useState<boolean>(
    !!question.comments
  );
  const [isModalOpen, setModalOpen] = useState<boolean>(false);
  const [isBottomSheetOpen, setBottomSheetOpen] = useState<boolean>(false);
  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);

  const scoringTypeValues = defaultScoringTypes[question.scoringTypeId];

  const performanceClassificationTagText = nonRenderableScoringTypes[question.scoringTypeId]
    ? `${question.performanceClassification} | ${nonRenderableScoringTypes[question.scoringTypeId]}`
    : question.performanceClassification;

  const handleTextAreaChange = (newValue: string) => {
    if (handleQuestionComment) {
      handleQuestionComment(newValue, question.id);
    }
  };

  function onClickArrow () {
    setStandardCommentputOpen(!standardCommentOpen);
  }

  function handleOpenBottomsheet () {
    setBottomSheetOpen(!isBottomSheetOpen);
  }

  function handleOpenModalImg () {
    setModalOpen(!isModalOpen);
  }

  function handleShowDeleteModal () {
    setShowDeleteModal(!showDeleteModal);
  }

  function handleViewPhoto () {
    setBottomSheetOpen(false);
    handleOpenModalImg();
  }

  function handleReplacePhoto () {
    setBottomSheetOpen(false);
    if (inputRef.current) {
      inputRef.current.click();
    }
  }

  function handleDeleteModal () {
    handleOpenBottomsheet();
    handleShowDeleteModal();
  }

  return (
    <>
      <Card>
        <TagSection>
          <Tag>{performanceClassificationTagText}</Tag>
          {showDropdownIcon && (
            <DropdownButton onClick={onClickArrow}>
              <DropdownIcon
                data-testid="expand-card-icon"
                rotate={standardCommentOpen ? '180' : '0'}
                color="battleShipGray"
              />
            </DropdownButton>
          )}
        </TagSection>
        <Question>
          {question.order === 0 ? question.text : `${question.order}. ${question.text}`}
        </Question>
        <IconSection>
          <IconContent>
            {scoringTypeValues &&
              scoringTypeValues.map((scoringType) => {
                return (
                  // tslint:disable-next-line:jsx-key
                  <ButtonWithIcon
                    onClick={() => {
                      if (onClickScore) {
                        onClickScore(
                          question.score === scoringType.value
                            ? undefined
                            : scoringType.value,
                          question.id,
                          question.scoringTypeId
                        );
                      }
                      if (!isView) {
                        setStandardCommentputOpen(
                          question.score !== scoringType.value
                        );
                      }
                    }}
                    backgroundColor={
                      question.score === scoringType.value
                        ? scoringType.color
                        : 'white'
                    }
                    borderColor={
                      question.score === scoringType.value
                        ? scoringType.color
                        : 'platinum'
                    }
                  >
                    {scoringType.icon === 'plus' && (
                      <Like
                        data-testid="score-met"
                        strokeColor={
                          question.score === scoringType.value
                            ? 'white'
                            : undefined
                        }
                        fillColor={
                          question.score === scoringType.value
                            ? scoringType.color
                            : undefined
                        }
                      />
                    )}
                    {scoringType.icon === 'minus' && (
                      <Dislike
                        data-testid="score-not-met"
                        strokeColor={
                          question.score === scoringType.value
                            ? 'white'
                            : undefined
                        }
                        fillColor={
                          question.score === scoringType.value
                            ? scoringType.color
                            : undefined
                        }
                      />
                    )}
                    {scoringType.icon === 'no' && (
                      <Note
                        data-testid="score-na"
                        strokeColor={
                          question.score === scoringType.value
                            ? 'white'
                            : undefined
                        }
                        fillColor={
                          question.score === scoringType.value
                            ? scoringType.color
                            : undefined
                        }
                      />
                    )}
                    {!scoringType.icon && scoringType.label}
                  </ButtonWithIcon>
                );
              })}
          </IconContent>

          <ImageButtonContainer>
            {question && !nonRenderableScoringTypes[question.scoringTypeId] && question.standardAttachment}
          </ImageButtonContainer>
        </IconSection>
        {standardCommentOpen && (
          <TextAreaContainer>
            <TextArea
              data-testid="standard-comment"
              textValue={question.comments}
              onValueChange={handleTextAreaChange}
              placeholder={t(
                'COMMON.MY_AUDIT.COMPONENTS.QUESTION.PLACEHOLDERS.ADD_YOUR_COMMENT'
              )}
              disabled={isView}
            />
          </TextAreaContainer>
        )}
      </Card>
      {isBottomSheetOpen && (
        <StandardsBottomSheet
          isBottomSheetOpen={isBottomSheetOpen}
          handleOpenBottomsheet={handleOpenBottomsheet}
          handleViewPhoto={handleViewPhoto}
          handleReplacePhoto={handleReplacePhoto}
          handleDeleteModal={handleDeleteModal}
        />
      )}
    </>
  );
}
